import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ENQUIRY_TYPES } from "@/lib/format";
import { ArrowLeft, Plus, Trash2, Mail, Users, Building2, KeyRound, Loader2, Save, ShieldCheck, Download, ScrollText, LogOut } from "lucide-react";
import { toast } from "sonner";

const ALL = ["All", ...ENQUIRY_TYPES];
const inp = "h-11 w-full rounded-xl border border-white/10 bg-black px-3 text-sm outline-none focus:border-[#2997ff]";

export default function OwnerSettings() {
  const navigate = useNavigate();
  const { logoutAll } = useAuth();
  const [s, setS] = useState(null);
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "", role: "staff" });
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/settings"), api.get("/auth/staff")]);
      setS(a.data); setStaff(b.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#2997ff]" /></div>;

  const saveSettings = async (patch) => {
    setBusy(true);
    try { const { data } = await api.put("/settings", patch); setS(data); toast.success("Saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const recips = s.enquiry_recipients || [];
  const setRecip = (i, key, val) => { const r = recips.map((x, idx) => idx === i ? { ...x, [key]: val } : x); setS({ ...s, enquiry_recipients: r }); };
  const toggleType = (i, t) => {
    const cur = recips[i].types || ["All"];
    let next = t === "All" ? ["All"] : (cur.includes(t) ? cur.filter((x) => x !== t && x !== "All") : [...cur.filter((x) => x !== "All"), t]);
    if (next.length === 0) next = ["All"];
    setRecip(i, "types", next);
  };

  const addStaff = async () => {
    if (!newStaff.name || !newStaff.email || !newStaff.password) return toast.error("Fill all fields");
    setBusy(true);
    try { await api.post("/auth/staff", newStaff); setNewStaff({ name: "", email: "", password: "", role: "staff" }); toast.success("User added (must change password on first login)"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const changeRole = async (id, role) => { try { await api.patch(`/auth/staff/${id}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const resetPw = async (id) => { const p = window.prompt("New temporary password (min 10 chars):"); if (!p) return; try { await api.post(`/auth/staff/${id}/reset-password`, { new_password: p }); toast.success("Password reset — user must change it next login"); } catch (e) { toast.error(apiError(e)); } };
  const removeUser = async (id) => { if (!window.confirm("Remove this user?")) return; try { await api.delete(`/auth/staff/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(apiError(e)); } };

  const exportCsv = () => { window.open(`${api.defaults.baseURL}/inventory/export.csv`, "_blank"); };
  const loadAudit = async () => { try { const { data } = await api.get("/audit"); setAudit(data); } catch (e) { toast.error(apiError(e)); } };
  const doLogoutAll = async () => { if (!window.confirm("Sign out of every device, including this one?")) return; await logoutAll(); };

  return (
    <div className="mf-fade-in pb-4">
      <button onClick={() => navigate("/dashboard")} className="mb-3 flex items-center gap-1 text-sm text-[#98989d]"><ArrowLeft size={16} /> Dashboard</button>
      <h1 className="mb-5 text-2xl font-extrabold tracking-tight">Owner settings</h1>

      {/* Enquiry recipients */}
      <section className="mb-5 rounded-3xl border border-white/10 bg-[#121214] p-4">
        <div className="mb-3 flex items-center gap-2 font-semibold"><Mail size={16} className="text-[#2997ff]" /> Enquiry recipients</div>
        <div className="space-y-3">
          {recips.map((r, i) => (
            <div key={i} data-testid={`recipient-${i}`} className="rounded-2xl border border-white/10 bg-black/40 p-3">
              <div className="flex gap-2">
                <input className={inp} value={r.email} onChange={(e) => setRecip(i, "email", e.target.value)} placeholder="email@domain.com" />
                <button onClick={() => setS({ ...s, enquiry_recipients: recips.filter((_, idx) => idx !== i) })} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#ff453a]/30 text-[#ff453a]"><Trash2 size={16} /></button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {ALL.map((t) => {
                  const on = (r.types || ["All"]).includes(t);
                  return <button key={t} onClick={() => toggleType(i, t)} className={`rounded-full px-2.5 py-1 text-xs font-medium ${on ? "bg-[#2997ff] text-white" : "border border-white/10 text-[#98989d]"}`}>{t}</button>;
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <button data-testid="add-recipient" onClick={() => setS({ ...s, enquiry_recipients: [...recips, { email: "", types: ["All"] }] })} className="flex items-center gap-1 rounded-xl border border-white/10 px-3 py-2 text-sm font-semibold"><Plus size={15} /> Add recipient</button>
          <button data-testid="save-recipients" onClick={() => saveSettings({ enquiry_recipients: s.enquiry_recipients })} disabled={busy} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#2997ff] py-2 text-sm font-semibold"><Save size={15} /> Save recipients</button>
        </div>
      </section>

      {/* Team & access */}
      <section className="mb-5 rounded-3xl border border-white/10 bg-[#121214] p-4">
        <div className="mb-3 flex items-center gap-2 font-semibold"><Users size={16} className="text-[#bf5af2]" /> Team & access</div>
        <div className="space-y-2">
          {staff.map((u) => (
            <div key={u.id} data-testid={`user-${u.id}`} className="flex items-center justify-between rounded-2xl border border-white/10 bg-black/40 p-3">
              <div className="min-w-0">
                <div className="truncate font-semibold">{u.name}</div>
                <div className="truncate text-xs text-[#98989d]">{u.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} className="h-9 rounded-lg border border-white/10 bg-black px-2 text-xs">
                  {["owner", "manager", "staff"].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <button onClick={() => resetPw(u.id)} title="Reset password" className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-[#98989d]"><KeyRound size={15} /></button>
                <button onClick={() => removeUser(u.id)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#ff453a]/30 text-[#ff453a]"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-black/40 p-3">
          <input data-testid="new-user-name" className={inp} placeholder="Name" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} />
          <input data-testid="new-user-email" className={inp} placeholder="Email" value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} />
          <input data-testid="new-user-password" className={inp} placeholder="Temp password (min 10 chars)" value={newStaff.password} onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })} />
          <select data-testid="new-user-role" className={inp} value={newStaff.role} onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}>
            {["owner", "manager", "staff"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button data-testid="add-user" onClick={addStaff} disabled={busy} className="col-span-2 flex items-center justify-center gap-2 rounded-xl bg-[#2997ff] py-2.5 text-sm font-semibold"><Plus size={15} /> Add user</button>
        </div>
        <p className="mt-2 text-xs text-[#98989d]">Owner: everything incl. cost & settings · Manager: inventory, prices & enquiries · Staff: add Macs, status, replies (no prices/cost).</p>
      </section>

      {/* Business details */}
      <section className="rounded-3xl border border-white/10 bg-[#121214] p-4">
        <div className="mb-3 flex items-center gap-2 font-semibold"><Building2 size={16} className="text-[#30d158]" /> Business details</div>
        <div className="space-y-3">
          <div><label className="mb-1 block text-xs text-[#98989d]">WhatsApp number (digits only, e.g. 919137408714)</label><input data-testid="biz-whatsapp" className={inp} value={s.whatsapp || ""} onChange={(e) => setS({ ...s, whatsapp: e.target.value })} /></div>
          <div><label className="mb-1 block text-xs text-[#98989d]">Phone numbers (comma separated)</label><input data-testid="biz-phones" className={inp} value={(s.phones || []).join(", ")} onChange={(e) => setS({ ...s, phones: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} /></div>
          <div><label className="mb-1 block text-xs text-[#98989d]">Address</label><input data-testid="biz-address" className={inp} value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs text-[#98989d]">Warranty — Apple silicon (days)</label><input data-testid="biz-warranty-apple" type="number" className={inp} value={s.warranty_apple ?? 60} onChange={(e) => setS({ ...s, warranty_apple: Number(e.target.value) })} /></div>
            <div><label className="mb-1 block text-xs text-[#98989d]">Warranty — Intel (days)</label><input data-testid="biz-warranty-intel" type="number" className={inp} value={s.warranty_intel ?? 45} onChange={(e) => setS({ ...s, warranty_intel: Number(e.target.value) })} /></div>
          </div>
        </div>
        <button data-testid="save-business" onClick={() => saveSettings({ whatsapp: s.whatsapp, phones: s.phones, address: s.address, warranty_apple: s.warranty_apple, warranty_intel: s.warranty_intel })} disabled={busy} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#2997ff] py-2.5 text-sm font-semibold"><Save size={15} /> Save business details</button>
      </section>

      {/* Security & data */}
      <section className="mt-5 rounded-3xl border border-white/10 bg-[#121214] p-4">
        <div className="mb-3 flex items-center gap-2 font-semibold"><ShieldCheck size={16} className="text-[#2997ff]" /> Security & data</div>

        <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-black/40 p-3">
          <div className="min-w-0 pr-3">
            <div className="text-sm font-semibold">Two-step login (email code)</div>
            <div className="text-xs text-[#98989d]">When on, everyone must enter a 6-digit code emailed to them after their password.</div>
          </div>
          <button
            data-testid="toggle-2fa"
            onClick={() => saveSettings({ two_factor_enabled: !s.two_factor_enabled })}
            disabled={busy}
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${s.two_factor_enabled ? "bg-[#30d158]" : "bg-white/15"}`}
          >
            <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-transform ${s.two_factor_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button data-testid="export-csv" onClick={exportCsv} className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-black/40 py-2.5 text-sm font-semibold"><Download size={15} /> Export inventory (CSV)</button>
          <button data-testid="logout-all" onClick={doLogoutAll} className="flex items-center justify-center gap-2 rounded-xl border border-[#ff453a]/30 py-2.5 text-sm font-semibold text-[#ff453a]"><LogOut size={15} /> Log out all devices</button>
        </div>
        <p className="mt-2 text-xs text-[#98989d]">The database is backed up automatically every night.</p>
      </section>

      {/* Audit log */}
      <section className="mt-5 rounded-3xl border border-white/10 bg-[#121214] p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold"><ScrollText size={16} className="text-[#bf5af2]" /> Activity log</div>
          <button data-testid="load-audit" onClick={loadAudit} className="rounded-full border border-white/10 px-3 py-1.5 text-xs font-semibold">{audit ? "Refresh" : "Show activity"}</button>
        </div>
        {audit && (
          <div className="max-h-80 space-y-1.5 overflow-y-auto">
            {audit.length === 0 && <div className="text-sm text-[#98989d]">No activity recorded yet.</div>}
            {audit.map((a) => (
              <div key={a.id} data-testid={`audit-${a.id}`} className="rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-xs">
                <div className="flex justify-between gap-2">
                  <span className="font-semibold text-[#2997ff]">{a.action}</span>
                  <span className="shrink-0 text-[#98989d]">{new Date(a.at).toLocaleString()}</span>
                </div>
                <div className="text-[#98989d]">{a.user_email} {a.detail ? `· ${a.detail}` : ""}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
